/**
 * Placement UX: the drawn tile floats above the board and follows the pointer. [P6-place-ux]
 *
 * Legal cells are computed locally via the engine's `legalPlacements` — no round trip, which is the
 * entire point of the engine being pure and shared. Rotation is `R` / right-click / a two-finger tap;
 * the tile snaps to the nearest legal cell for the current rotation and visibly refuses illegal ones.
 * Pointer events throughout, so mouse and touch both work end to end.
 */

import { Plane, Raycaster, Vector2, Vector3 } from 'three';
import {
  placementConflicts,
  Side,
  type PlacementConflict,
  type Pos,
  type Rotation,
  type Terrain,
} from '@carc/engine';
import type { Scene } from './scene.js';
import type { BoardView } from './board-view.js';
import type { PlayableSession } from './session.js';
import type { Toaster } from './toast.js';
import type { SoundBoard } from './audio.js';
import { createTileMesh, worldPosition, worldRotationY, type TileMesh } from './tile-mesh.js';
import { tween, TweenGroup } from './tween.js';

const SIDE_NAME: Record<Side, string> = {
  [Side.North]: 'top',
  [Side.East]: 'right',
  [Side.South]: 'bottom',
  [Side.West]: 'left',
};

const TERRAIN_NAME: Record<Terrain, string> = {
  city: 'city',
  road: 'road',
  field: 'field',
};

const HOVER_HEIGHT = 0.42;
const CLICK_SLOP_PX = 10;

export class PlacementController {
  private readonly untick: () => void;
  private floating: TileMesh | null = null;
  private rotation: Rotation = 0;
  private enabled = false;

  private legalForRotation: { pos: Pos; rotation: Rotation }[] = [];
  private legalAll: { pos: Pos; rotation: Rotation }[] = [];
  private snapPos: Pos | null = null;
  private snapValid = false;

  private readonly pointerWorld = new Vector3();
  private readonly raycaster = new Raycaster();
  private readonly groundPlane = new Plane(new Vector3(0, 1, 0), 0);
  private readonly activePointers = new Map<number, { x: number; y: number }>();
  private downInfo: { x: number; y: number } | null = null;
  /** A right-button or two-finger press that has not yet decided whether it is a rotate or a camera drag. */
  private rotateCandidate: { x: number; y: number } | null = null;
  private readonly tweens = new TweenGroup();

  constructor(
    private readonly scene: Scene,
    private readonly board: BoardView,
    private readonly session: PlayableSession,
    private readonly toaster: Toaster,
    private readonly sounds: SoundBoard,
    private readonly onPlaced: () => void,
    private readonly onRotationChange?: (rotation: Rotation) => void,
  ) {
    const canvas = scene.renderer.domElement;
    canvas.addEventListener('pointermove', this.handleMove);
    canvas.addEventListener('pointerdown', this.handleDown);
    canvas.addEventListener('pointerup', this.handleUp);
    canvas.addEventListener('pointercancel', this.handleCancel);
    canvas.addEventListener('contextmenu', this.handleContextMenu);
    window.addEventListener('keydown', this.handleKey);
    this.untick = scene.onTick((dt) => this.tick(dt));
  }

  /**
   * Unbind from the canvas and the scene clock.
   *
   * A controller that outlives its session keeps its listeners, so a second one means TWO floating
   * tiles tracking the same pointer — the duplicate held tile. Ownership is per-session, so the
   * session teardown must be able to let go.
   */
  dispose(): void {
    this.deactivate();
    const canvas = this.scene.renderer.domElement;
    canvas.removeEventListener('pointermove', this.handleMove);
    canvas.removeEventListener('pointerdown', this.handleDown);
    canvas.removeEventListener('pointerup', this.handleUp);
    canvas.removeEventListener('pointercancel', this.handleCancel);
    canvas.removeEventListener('contextmenu', this.handleContextMenu);
    window.removeEventListener('keydown', this.handleKey);
    this.untick();
  }

  get isActive(): boolean {
    return this.enabled;
  }

  activate(): void {
    const state = this.session.state;
    if (!state.currentTile) return;
    const def = this.session.tiles.get(state.currentTile.defId);
    if (!def) return;

    // Already showing this tile? Leave it alone. `advance()` can run twice for the same phase, and
    // re-activating used to orphan the previous floating mesh in the scene (a permanent ghost tile)
    // and silently reset the player's rotation back to 0 mid-decision.
    if (this.enabled && this.floating) return;
    if (this.floating) this.deactivate();

    this.enabled = true;
    this.rotation = 0;
    this.onRotationChange?.(this.rotation);
    this.floating = createTileMesh(def, state.currentTile.drawIndex);
    this.floating.group.position.set(0, 2.6, 0);
    this.board.root.add(this.floating.group);

    this.refreshLegalCells();

    // "tile draw — the tile rises into the hand"
    const startY = 2.6;
    this.tweens.add(
      tween(360, (t) => {
        if (this.floating) this.floating.group.position.y = startY * (1 - t) + HOVER_HEIGHT * t;
      }),
    );
  }

  deactivate(): void {
    this.enabled = false;
    this.downInfo = null;
    this.rotateCandidate = null;
    this.activePointers.clear();
    if (this.floating) {
      // Dispose, not just detach: the held tile is rebuilt every turn, so merely removing it from
      // the scene leaked ~4MB of canvas and GPU texture per turn for the whole game.
      this.floating.dispose();
      this.floating = null;
    }
    this.board.clearLegalCells();
  }

  private refreshLegalCells(): void {
    this.legalAll = this.session.legalPlacementsForCurrentTile();
    this.legalForRotation = this.legalAll.filter((p) => p.rotation === this.rotation);
    // Show BOTH: where it fits right now, and where it would fit after a turn. Previously only the
    // current rotation was drawn, so most of the legal board was invisible and rotation looked broken.
    this.board.showLegalCells(
      this.legalForRotation.map((p) => p.pos),
      this.legalAll.map((p) => p.pos),
    );
    this.updateSnap();
  }

  private updateSnap(): void {
    // Snap to the nearest cell the tile could EVER occupy, not just those it fits at this rotation.
    // Snapping only to currently-valid cells drags the tile away from wherever you point, which reads
    // as "it won't let me put it there" when the real answer is "turn it first". Now it parks where you
    // point, shows red if it doesn't fit yet, and turns green in place when you rotate.
    const candidates = this.legalAll;
    if (candidates.length === 0) {
      this.snapPos = null;
      this.snapValid = false;
      return;
    }
    let best = candidates[0] as { pos: Pos; rotation: Rotation };
    let bestDist = Infinity;
    for (const c of candidates) {
      const [wx, , wz] = worldPosition(c.pos);
      const d = (wx - this.pointerWorld.x) ** 2 + (wz - this.pointerWorld.z) ** 2;
      if (d < bestDist) {
        bestDist = d;
        best = c;
      }
    }
    this.snapPos = best.pos;
    this.snapValid = this.legalForRotation.some((p) => p.pos.x === best.pos.x && p.pos.y === best.pos.y);
  }

  private updatePointerWorld(clientX: number, clientY: number): void {
    const canvas = this.scene.renderer.domElement;
    const rect = canvas.getBoundingClientRect();
    const ndc = new Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(ndc, this.scene.camera);
    const hit = new Vector3();
    if (this.raycaster.ray.intersectPlane(this.groundPlane, hit)) {
      this.pointerWorld.copy(hit);
    }
  }

  private tick(dt: number): void {
    this.tweens.update(dt);
    if (!this.enabled || !this.floating) return;

    const damp = 1 - Math.pow(0.001, dt);
    if (this.snapPos) {
      const [x, , z] = worldPosition(this.snapPos);
      const p = this.floating.group.position;
      p.x += (x - p.x) * damp;
      p.z += (z - p.z) * damp;
      p.y += (HOVER_HEIGHT - p.y) * Math.min(1, damp * 1.3);
    }

    const targetRotY = worldRotationY(this.rotation);
    let diff = targetRotY - this.floating.group.rotation.y;
    diff = Math.atan2(Math.sin(diff), Math.cos(diff));
    this.floating.group.rotation.y += diff * Math.min(1, damp * 1.6);

    this.floating.setHighlight(true, this.snapValid);
  }

  /** Same path the `R` key / right-click / two-finger tap use — exported for the HUD's rotate button. */
  rotate(): void {
    if (!this.enabled) return;
    this.rotation = ((this.rotation + 1) % 4) as Rotation;
    this.onRotationChange?.(this.rotation);
    this.refreshLegalCells();
  }

  private shakeInvalid(): void {
    if (!this.floating) return;
    const group = this.floating.group;
    this.tweens.add(
      tween(240, (t) => {
        group.position.x += Math.sin(t * Math.PI * 5) * 0.05 * (1 - t) * 0.16;
      }),
    );
  }

  /**
   * Say WHICH side is wrong, not merely that something is.
   *
   * "That tile doesn't fit there" is true and useless: a cell can have four neighbours, and a player
   * who has checked the two they can see — and found both matching — will read the refusal as a bug.
   * If another rotation would work, say that instead, because then rotating really is the answer.
   */
  private explainRefusal(pos: Pos): string {
    const defId = this.session.state.currentTile?.defId;
    if (!defId) return "That tile doesn't fit there.";

    const fitsAfterTurning = ([0, 1, 2, 3] as Rotation[]).some(
      (r) => r !== this.rotation && placementConflicts(this.session.state, this.session.tiles, defId, pos, r).length === 0,
    );
    if (fitsAfterTurning) {
      return 'Not at this angle — turn the tile (R, right-click, or two-finger tap) and it will fit here.';
    }

    const conflicts = placementConflicts(this.session.state, this.session.tiles, defId, pos, this.rotation);
    if (conflicts.length === 0) return "That tile doesn't fit there.";

    const first = conflicts[0] as PlacementConflict;
    const where = SIDE_NAME[first.side];
    const more = conflicts.length > 1 ? ` (and ${conflicts.length - 1} more side${conflicts.length > 2 ? 's' : ''})` : '';
    return `Doesn't fit: its ${where} side is ${TERRAIN_NAME[first.mine]}, but the tile there shows ${TERRAIN_NAME[first.theirs]}${more}.`;
  }

  private confirm(): void {
    if (!this.enabled || !this.snapPos) return;
    if (!this.snapValid) {
      this.sounds.play('invalid');
      this.toaster.error(this.explainRefusal(this.snapPos));
      this.shakeInvalid();
      return;
    }
    const pos = this.snapPos;
    const rotation = this.rotation;
    const result = this.session.placeTile(pos, rotation);
    if (!result.ok) {
      this.sounds.play('invalid');
      this.toaster.error(result.error.message);
      return;
    }
    this.deactivate();
    this.onPlaced();
  }

  private handleMove = (event: PointerEvent): void => {
    if (!this.enabled) return;
    this.updatePointerWorld(event.clientX, event.clientY);
    this.updateSnap();
  };

  /**
   * Rotation is a deliberate TAP — never merely "a second finger touched" or "the right button is
   * down". [P6-camera]
   *
   * Both gestures used to fire the moment the pointer went down, which quietly took the two gestures a
   * player needs for the camera and gave them to the tile: a two-finger pinch or drag (the natural way
   * to zoom and move a board) rotated the tile instead, and right-drag — OrbitControls' pan — could
   * never begin. Tony reached to zoom and moved the browser instead, then could not get the board back.
   * Requiring the pointer to come back up near where it went down leaves every drag to the camera.
   */
  private handleDown = (event: PointerEvent): void => {
    if (!this.enabled) return;
    // A touch produces no `pointermove` before it lands, so without this the snap target is still
    // wherever the pointer was last seen — on the first tap of a game, the world origin. The tile then
    // drops somewhere other than where the player tapped, which on a phone is the whole interaction.
    this.updatePointerWorld(event.clientX, event.clientY);
    this.updateSnap();
    this.activePointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (event.button === 2) {
      this.rotateCandidate = { x: event.clientX, y: event.clientY };
      return;
    }
    if (event.pointerType === 'touch' && this.activePointers.size === 2) {
      // Two fingers down: this may become a rotate tap, or it may become a pinch/pan. Do not decide
      // yet — and cancel the single-finger tap that was in progress, since it is now part of a
      // two-finger gesture.
      this.rotateCandidate = { x: event.clientX, y: event.clientY };
      this.downInfo = null;
      return;
    }
    this.downInfo = { x: event.clientX, y: event.clientY };
  };

  private handleUp = (event: PointerEvent): void => {
    const down = this.activePointers.get(event.pointerId);
    this.activePointers.delete(event.pointerId);
    if (!this.enabled) return;

    // A right-click or two-finger tap that stayed put is a rotate; one that travelled was the player
    // moving the camera, and the tile must not spin under them.
    const candidate = this.rotateCandidate;
    if (candidate && (event.button === 2 || event.pointerType === 'touch')) {
      this.rotateCandidate = null;
      const travelled = Math.hypot(event.clientX - candidate.x, event.clientY - candidate.y);
      const ownTravel = down ? Math.hypot(event.clientX - down.x, event.clientY - down.y) : 0;
      if (Math.max(travelled, ownTravel) <= CLICK_SLOP_PX) this.rotate();
      return;
    }
    if (event.button === 2) return;

    const info = this.downInfo;
    this.downInfo = null;
    if (!info) return;
    const moved = Math.hypot(event.clientX - info.x, event.clientY - info.y);
    if (moved > CLICK_SLOP_PX) return; // a camera drag, not a tap
    this.confirm();
  };

  private handleCancel = (event: PointerEvent): void => {
    this.activePointers.delete(event.pointerId);
    this.downInfo = null;
    this.rotateCandidate = null;
  };

  private handleContextMenu = (event: MouseEvent): void => {
    if (this.enabled) event.preventDefault();
  };

  private handleKey = (event: KeyboardEvent): void => {
    if (!this.enabled) return;
    if (event.key.toLowerCase() === 'r') {
      event.preventDefault();
      this.rotate();
    }
  };
}
